
import React from "react";
import { Shield, ShieldAlert, ShieldCheck, ShieldX } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface BackupSecurityStatusProps {
  isScanned: boolean;
  isClean?: boolean;
  threatDetails?: string;
  scanDate?: string;
  className?: string;
}

const BackupSecurityStatus: React.FC<BackupSecurityStatusProps> = ({
  isScanned,
  isClean,
  threatDetails,
  scanDate,
  className
}) => {
  // Format scan date if available
  const formattedDate = scanDate ? new Date(scanDate).toLocaleString() : '';
  
  if (!isScanned) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn("inline-flex items-center text-gray-400", className)}>
              <Shield className="h-4 w-4 mr-1" />
              <span className="text-xs">Not scanned</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>This backup has not been scanned for security threats</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  if (isClean) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn("inline-flex items-center text-green-600", className)}>
              <ShieldCheck className="h-4 w-4 mr-1" />
              <span className="text-xs">Secure</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>This backup has been scanned and is secure</p>
            {formattedDate && <p className="text-xs text-gray-400 mt-1">Scanned: {formattedDate}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("inline-flex items-center text-red-600", className)}>
            <ShieldAlert className="h-4 w-4 mr-1" />
            <span className="text-xs">Threat detected</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Security threat detected in this backup:</p>
          <p className="font-medium">{threatDetails || "Unknown threat"}</p>
          {formattedDate && <p className="text-xs text-gray-400 mt-1">Scanned: {formattedDate}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BackupSecurityStatus;
